import {
  List,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AddFeedEventIcon,
  AddFeedPurchaseIcon,
  SwardCompositionIcon,
} from "common/icons";
import FertilizerEventIcon from "common/icons/FertilizerEventIcon";
import FieldPreparationEventIcon from "common/icons/FieldPreparationEventIcon";
import YieldEventIcon from "common/icons/YieldEventIcon";
import { useTranslation } from "common/locales";
import { getFormattedDate } from "common/utils/getFormattedTime";
import {
  FertilizerEvent,
  FieldPreparationEvent,
  GeneralFeedEvent,
  GeneralFeedPurchaseEvent,
  ReseedEvent,
  selectAllFeedEvents,
  selectAllFeedPurchaseEvents,
  selectAllFertilizerEvents,
  selectAllFieldPreparationEvents,
  selectAllReseedEvents,
  selectAllYieldEvents,
  useAppSelector,
  YieldEvent,
} from "model";
import CombinedEventListItem from "./CombinedEventListItem";
import EventActionMenu from "./EventActionMenu";
import {
  FeedEventTableCell,
  FeedPurchaseEventTableCell,
  FertilizerEventTableCell,
  FieldPreparationEventTableCell,
  ReseedEventTableCell,
  YieldEventTableCell,
} from "./TableCellListItems";

export type CombinedEvent =
  | { type: "reseed"; event: ReseedEvent; date: Date }
  | { type: "feed"; event: GeneralFeedEvent; date: Date }
  | { type: "feedPurchase"; event: GeneralFeedPurchaseEvent; date: Date }
  | { type: "yield"; event: YieldEvent; date: Date }
  | { type: "fertilizer"; event: FertilizerEvent; date: Date }
  | { type: "fieldPreparation"; event: FieldPreparationEvent; date: Date };

export type EventType =
  | "reseed"
  | "feed"
  | "feedPurchase"
  | "yield"
  | "fertilizer"
  | "fieldPreparation";

interface CombinedInputsOutputsListProps {
  selectedEventTypes?: EventType[];
}

export default function CombinedInputsOutputsList({
  selectedEventTypes = [],
}: CombinedInputsOutputsListProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const locale = useAppSelector((state) => state.app.locale);

  // Get all events from Redux
  const reseedEvents: Array<ReseedEvent> = useAppSelector((state) =>
    selectAllReseedEvents(state)
  );
  const feedEvents: Array<GeneralFeedEvent> = useAppSelector((state) =>
    selectAllFeedEvents(state)
  );
  const feedPurchaseEvents: Array<GeneralFeedPurchaseEvent> = useAppSelector(
    (state) => selectAllFeedPurchaseEvents(state)
  );
  const yieldEvents: Array<YieldEvent> = useAppSelector((state) =>
    selectAllYieldEvents(state)
  );
  const fertilizerEvents: Array<FertilizerEvent> = useAppSelector((state) =>
    selectAllFertilizerEvents(state)
  );
  const fieldPreparationEvents: Array<FieldPreparationEvent> = useAppSelector(
    (state) => selectAllFieldPreparationEvents(state)
  );

  // Combine all events with their dates
  const combinedEvents: CombinedEvent[] = [
    ...reseedEvents.map((event) => ({
      type: "reseed" as const,
      event,
      date: new Date(event.reseedDate),
    })),
    ...feedEvents.map((event) => ({
      type: "feed" as const,
      event,
      date: new Date(event.feedEventDate),
    })),
    ...feedPurchaseEvents.map((event) => ({
      type: "feedPurchase" as const,
      event,
      date: new Date(event.feedPurchaseEventDate),
    })),
    ...yieldEvents.map((event) => ({
      type: "yield" as const,
      event,
      date: new Date(event.yieldEventDate),
    })),
    ...fertilizerEvents.map((event) => ({
      type: "fertilizer" as const,
      event,
      date: new Date(event.fertilizerApplicationDate),
    })),
    ...fieldPreparationEvents.map((event) => ({
      type: "fieldPreparation" as const,
      event,
      date: new Date(event.fieldPreparationEventDate),
    })),
  ];

  // Filter events by selected types (if filter is active)
  const filteredEvents =
    selectedEventTypes.length > 0
      ? combinedEvents.filter((event) =>
          selectedEventTypes.includes(event.type)
        )
      : combinedEvents;

  // Sort by date descending (most recent first)
  const sortedEvents = filteredEvents.sort(
    (a, b) => b.date.valueOf() - a.date.valueOf()
  );

  const getEventIcon = (type: CombinedEvent["type"]) => {
    switch (type) {
      case "reseed":
        // SwardCompositionIcon has hardcoded colors (green ladybug), keep colorful
        return <SwardCompositionIcon />;
      case "feed":
        // No htmlColor - uses theme default (red)
        return <AddFeedEventIcon />;
      case "feedPurchase":
        // No htmlColor - uses theme default (red)
        return <AddFeedPurchaseIcon />;
      case "yield":
        // No htmlColor - uses theme default (red)
        return <YieldEventIcon />;
      case "fertilizer":
        // No htmlColor - uses theme default (red)
        return <FertilizerEventIcon />;
      case "fieldPreparation":
        // No htmlColor - uses theme default (red)
        return <FieldPreparationEventIcon />;
    }
  };

  const getEventTypeLabel = (type: CombinedEvent["type"]) => {
    switch (type) {
      case "reseed":
        return "Reseed";
      case "feed":
        return "Feed";
      case "feedPurchase":
        return "Feed Purchase";
      case "yield":
        return "Yield";
      case "fertilizer":
        return "Fertilizer";
      case "fieldPreparation":
        return "Field Preparation";
    }
  };

  const getEventDetails = (combinedEvent: CombinedEvent) => {
    switch (combinedEvent.type) {
      case "reseed":
        return <ReseedEventTableCell reseedEvent={combinedEvent.event} />;
      case "feed":
        return <FeedEventTableCell feedEvent={combinedEvent.event} />;
      case "feedPurchase":
        return (
          <FeedPurchaseEventTableCell feedPurchaseEvent={combinedEvent.event} />
        );
      case "yield":
        return <YieldEventTableCell yieldEvent={combinedEvent.event} />;
      case "fertilizer":
        return (
          <FertilizerEventTableCell fertilizerEvent={combinedEvent.event} />
        );
      case "fieldPreparation":
        return (
          <FieldPreparationEventTableCell
            fieldPreparationEvent={combinedEvent.event}
          />
        );
    }
  };

  // Mobile: use List view
  if (isMobile) {
    return (
      <Paper>
        <List sx={{ py: 0 }}>
          {sortedEvents.map((combinedEvent) => (
            <CombinedEventListItem
              key={`combined-${combinedEvent.type}-${combinedEvent.event.guid}`}
              combinedEvent={combinedEvent}
              getEventIcon={getEventIcon}
              getEventTypeLabel={getEventTypeLabel}
            />
          ))}
        </List>
      </Paper>
    );
  }

  // Desktop: use Table view
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "60px" }}></TableCell>
            <TableCell>Time & Date</TableCell>
            <TableCell>Event Type</TableCell>
            <TableCell>Details</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEvents.map((combinedEvent) => {
            const key = `combined-${combinedEvent.type}-${combinedEvent.event.guid}`;
            const formattedDate = getFormattedDate(
              combinedEvent.date.toISOString(),
              locale
            );

            return (
              <TableRow key={key}>
                <TableCell sx={{ verticalAlign: "top", pt: 2 }}>
                  {getEventIcon(combinedEvent.type)}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  {formattedDate}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  {getEventTypeLabel(combinedEvent.type)}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top", p: 0 }}>
                  {getEventDetails(combinedEvent)}
                </TableCell>
                <EventActionMenu combinedEvent={combinedEvent} />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Export helper to get filtered and sorted events
export function useCombinedEvents(selectedEventTypes: EventType[] = []) {
  const locale = useAppSelector((state) => state.app.locale);

  const reseedEvents: Array<ReseedEvent> = useAppSelector((state) =>
    selectAllReseedEvents(state)
  );
  const feedEvents: Array<GeneralFeedEvent> = useAppSelector((state) =>
    selectAllFeedEvents(state)
  );
  const feedPurchaseEvents: Array<GeneralFeedPurchaseEvent> = useAppSelector(
    (state) => selectAllFeedPurchaseEvents(state)
  );
  const yieldEvents: Array<YieldEvent> = useAppSelector((state) =>
    selectAllYieldEvents(state)
  );
  const fertilizerEvents: Array<FertilizerEvent> = useAppSelector((state) =>
    selectAllFertilizerEvents(state)
  );
  const fieldPreparationEvents: Array<FieldPreparationEvent> = useAppSelector(
    (state) => selectAllFieldPreparationEvents(state)
  );

  const combinedEvents: CombinedEvent[] = [
    ...reseedEvents.map((event) => ({
      type: "reseed" as const,
      event,
      date: new Date(event.reseedDate),
    })),
    ...feedEvents.map((event) => ({
      type: "feed" as const,
      event,
      date: new Date(event.feedEventDate),
    })),
    ...feedPurchaseEvents.map((event) => ({
      type: "feedPurchase" as const,
      event,
      date: new Date(event.feedPurchaseEventDate),
    })),
    ...yieldEvents.map((event) => ({
      type: "yield" as const,
      event,
      date: new Date(event.yieldEventDate),
    })),
    ...fertilizerEvents.map((event) => ({
      type: "fertilizer" as const,
      event,
      date: new Date(event.fertilizerApplicationDate),
    })),
    ...fieldPreparationEvents.map((event) => ({
      type: "fieldPreparation" as const,
      event,
      date: new Date(event.fieldPreparationEventDate),
    })),
  ];

  const filteredEvents =
    selectedEventTypes.length > 0
      ? combinedEvents.filter((event) =>
          selectedEventTypes.includes(event.type)
        )
      : combinedEvents;

  const sortedEvents = filteredEvents.sort(
    (a, b) => b.date.valueOf() - a.date.valueOf()
  );

  return sortedEvents;
}
